import { Button } from '@timelyapp/react';
import classNames from 'classnames';
import DiagnosticResourceId from 'components/dev-tools/DiagnosticResourceId';
import Label from 'components/Label';
import Tooltip from 'components/Tooltip';
import type { FactoryApp } from 'factories/apps';
import React from 'react';
import FontAwesome from 'react-fontawesome';
import { connect } from 'react-redux';
import { fetchAppAccounts } from 'redux/modules/apps.actions';
import { makeGetAppAccountsById } from 'redux/selectors/apps';
import type { IState } from 'redux/state';
import type { AppAccount } from 'types/apps';
import type { MappedActions } from 'types/redux';
import AppIcon from './AppIcon';
import styles from './AppsListItem.module.scss';

export interface ExternalProps {
  className?: string;
  app: FactoryApp;
  variant: 'small' | 'large';
  onClick: (appId: number) => void;
}

const mapActionsToProps = {
  fetchAppAccounts,
};

const makeMapStateToProps = () => {
  const getAppAccounts = makeGetAppAccountsById();
  const mapStateToProps = (state: IState, ownProps: ExternalProps) => {
    return { appAccounts: getAppAccounts(state, { id: ownProps.app.appId }) };
  };
  return mapStateToProps;
};

interface StateProps {
  appAccounts: Array<AppAccount>;
}

type ActionProps = MappedActions<typeof mapActionsToProps>;
type Props = ExternalProps & StateProps & ActionProps;

class AppsListItem extends React.Component<Props> {
  componentDidMount() {
    if (this.props.app.ui.isEnabled) {
      this.props.fetchAppAccounts(this.props.app.appId);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.app.ui.isEnabled && !prevProps.app.ui.isEnabled) {
      this.props.fetchAppAccounts(this.props.app.appId);
    }
  }

  handleClick = () => {
    this.props.onClick(this.props.app.appId);
  };

  renderMeta() {
    const { integrationsCount, importedDate, integrated, description, ui } = this.props.app;
    const { appAccounts, variant } = this.props;
    const disconnectedEmails = appAccounts?.filter(account => account.disconnected);

    if (ui.isDisconnected) {
      return (
        <Label
          uppercase={false}
          className={styles.disconnectedWarning}
          size="medium"
          variant="muted-danger"
        >
          <FontAwesome style={{ marginRight: 5 }} name="exclamation-triangle" />
          {disconnectedEmails?.map((account, i) => {
            if (disconnectedEmails.length > 1 && i !== disconnectedEmails.length - 1) {
              return (
                <p key={account.id}>
                  {account.disconnected_reason} · {account.email},{' '}
                </p>
              );
            }
            return variant === 'small' ? (
              <Tooltip title={`Connection lost · ${account.email}`} key={account.id}>
                <p className={styles.tooltipText}>Connection lost · {account.email}</p>
              </Tooltip>
            ) : (
              <p key={account.id}>Connection lost · {account.email}</p>
            );
          })}
        </Label>
      );
    }

    if (!integrated) {
      return <p className={styles.description}>{description}</p>;
    }

    return (
      <p className={styles.description}>
        <span>{`${integrationsCount} account${integrationsCount !== 1 ? 's' : ''}`}</span>
        {importedDate && (
          <span>
            <span className={styles.spacer}>&middot;</span>
            <span>Data received {importedDate}</span>
          </span>
        )}
      </p>
    );
  }

  renderInfoArea() {
    const { variant, app } = this.props;
    const { createdDate, ui } = app;

    if (ui.isInvited) {
      return <Button priority="xhigh">Connect account</Button>;
    }

    if (ui.isDisconnected && variant !== 'small') {
      return <Button priority="xhigh">Review</Button>;
    }

    if (variant !== 'small' && createdDate && !ui.isDisconnected) {
      return <p className={styles.createdDate}>Added {createdDate}</p>;
    }
  }

  render() {
    const { className, app } = this.props;

    return (
      <div
        data-testid={`integration_apps_${app.id}`}
        className={classNames(className, styles.container)}
        onClick={this.handleClick}
      >
        <div className={styles.icon}>
          <AppIcon path={app.logoUrl} isLockedFeature={app.ui.isLockedFeature} />
        </div>
        <div className={styles.meta}>
          <p className={styles.title}>
            <DiagnosticResourceId resource="app" id={app.appId} />
            <DiagnosticResourceId resource="" id={app.id} />
            {app.displayName}
          </p>
          {this.renderMeta()}
        </div>
        {this.renderInfoArea()}
      </div>
    );
  }
}

export default connect<StateProps, typeof mapActionsToProps, ExternalProps, IState>(
  makeMapStateToProps,
  mapActionsToProps
)(AppsListItem);
